import React, { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import { useCart } from '../context/CartContext.tsx';
import './Navbar.css';
import { Link } from 'react-router-dom';
import Login from '../Pages/Login.tsx';
import { HiOutlineUserCircle } from 'react-icons/hi2';

import { User } from '../types';

interface NavbarProps {
  closeMenu: () => void;
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
}

const Navbar: React.FC<NavbarProps> = ({ closeMenu, user, setUser }) => {
  const { cartItems } = useCart();
  const totalItems = cartItems.reduce((total, item) => total + item.quantity, 0);

  const storedUser = localStorage.getItem('user');
  const storedSessionUser = sessionStorage.getItem('user');

  // Initiera user från sessionStorage om den finns, annars null
  const initialUser: User | null = storedSessionUser ? JSON.parse(storedSessionUser) : null;

  const [localUser, setLocalUser] = useState<User | null>(initialUser);
  const [showLogin, setShowLogin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Klick utanför dropdown stänger den
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Om sessionStorage tas bort ska localStorage också tas bort
// --- Lägg till en useEffect som lyssnar på user-prop ---
useEffect(() => {
  if (user) {
    setLocalUser(user);
    sessionStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('user', JSON.stringify(user));
    console.log('Navbar uppdaterad med ny user:', user);
  }
}, [user]);


const handleLoginSuccess = (userData: User | null) => {
  if (userData) {
    const fixedUser: User = {
      username: userData.username || "Unknown",
      userId: userData.userId,
      email: userData.email || "",
      token: userData.token,
    };

    // Spara i både sessionStorage och localStorage
    sessionStorage.setItem('user', JSON.stringify(fixedUser));
    localStorage.setItem('user', JSON.stringify(fixedUser));

    console.log('Användare sparad i sessionStorage och localStorage:', fixedUser);

    setUser(fixedUser);
    setLocalUser(fixedUser);
  } else {
    setUser(null);
    setLocalUser(null);
    sessionStorage.removeItem('user');
    localStorage.removeItem('user');
    console.log('Användare utloggad, sessionStorage och localStorage rensad');
  }

  setShowLogin(false);
  setShowDropdown(false);
};


  const handleLogout = () => {
    setUser(null);
    setLocalUser(null);
    setShowDropdown(false);
    sessionStorage.removeItem("user");
    localStorage.removeItem("user");
  };

  return (
    <header className="navbar-container">
<div className="logo-container">
  <h1 className="logo">
   DesignLocker<br />
  </h1>
</div>


      <div className="menu-icon" onClick={() => setMenuOpen(!menuOpen)}>☰</div>

      <nav>
        <ul className={`nav-links ${menuOpen ? 'active' : ''}`}>
          <li><Link to="/" className="nav-link" onClick={() => { setMenuOpen(false); closeMenu(); }}>Hem</Link></li>
          <li><Link to="/productlist" className="nav-link" onClick={() => { setMenuOpen(false); closeMenu(); }}>Produkter</Link></li>
          <li><Link to="/about" className="nav-link" onClick={() => { setMenuOpen(false); closeMenu(); }}>Om oss</Link></li>
          <li><Link to="/contact" className="nav-link" onClick={() => { setMenuOpen(false); closeMenu(); }}>Kontakt</Link></li>
          <li className="cart-link-mobile">
            <Link to="/cart" className="nav-link" onClick={() => { setMenuOpen(false); closeMenu(); }}>🛒 ({totalItems})</Link>
          </li>
        </ul>
      </nav>

      <div className="right-section">
        <div className="cart-container desktop-only">
          <Link to="/cart" className="nav-link cart-link" onClick={closeMenu}>🛒 ({totalItems})</Link>
        </div>

        <div className="login-container" ref={dropdownRef}>
          <button
            onClick={() => {
              if (localUser) {
                setShowDropdown(prev => !prev);
                setShowLogin(false);
              } else {
                setShowLogin(true);
                setShowDropdown(false);
              }
            }}
            className="login-icon-button"
            aria-label="Användarmenyn"
          >
            <HiOutlineUserCircle size={28} />
          </button>

          {localUser && showDropdown && (
            <div className="user-menu">
              <Link to="/orders" className="orders-link" onClick={() => setShowDropdown(false)}>Orderhistorik</Link>
              <Link to="/profile" className="orders-link" onClick={() => setShowDropdown(false)}>Inställningar</Link>
              <button onClick={handleLogout} className="logout-button">Logga ut</button>
              <div className="user-info-bottom">
                <span className="user-status">Inloggad som:</span>
                <span className="user-name">{localUser.username}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {!localUser && showLogin && (
        <div className="login-popup">
          <Login setUser={handleLoginSuccess} onClose={() => setShowLogin(false)} />
        </div>
      )}
    </header>
  );
};

export default Navbar;
